import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { Check, MessageCircle } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { renderTemplate } from '@/lib/mergeTags';
import { formatDay, formatRelative } from '@/lib/relativeTime';
import type { EveryoneFeedItem, FriendProgressTone, FriendStep } from '@/types/referral';

import { Avatar } from './Avatar';
import { Button } from './Button';
import { Heading } from './Heading';
import { Overline } from './Overline';
import { Text } from './Text';
import { canStillFinish, stepLineFor } from '@/lib/friendProgress';
import { initialsFor } from '@/lib/initials';

export interface ReferralDetailSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** The feed row whose detail to render. Null when the sheet is closed. */
  item: EveryoneFeedItem | null;
  /** Opens the nudge flow preselected to this person. */
  onNudge: (item: EveryoneFeedItem) => void;
  /**
   * Removes this person from the ambassador's list (soft hide). Omit for a
   * read-only sheet (the invite screen): Remove and its confirm never render.
   */
  onRemove?: (item: EveryoneFeedItem) => void;
  /**
   * Open straight into the confirm state. Set when the sheet was reached by
   * swiping a row — the swipe already expressed intent, so it lands on
   * "Remove X from your list?" rather than making the user find Remove again.
   */
  startConfirming?: boolean;
  /**
   * The deal's authored nudge text (`marketing_copy.nudge_template`), shown
   * under the Text button so the ambassador knows what they are about to
   * send. Absent on the invite screen, which has no deal.
   */
  nudgeTemplate?: string | null;
  testID?: string;
}

/**
 * Bottom-sheet detail for a friend in the Refer tab (mock D1, 2026-09-13).
 * Reads the `EveryoneFeedItem` the list already holds — `progress` carries
 * the steps and dates — so opening it costs no fetch and has no loading
 * or error state.
 *
 *   identity      name · "Opened SoFi Sep 9" · channel
 *   status card   the row's two lines, in a sentence, tinted by tone
 *   their steps   the deal's steps with the day each was confirmed
 *   Text          primary, with the prefilled message underneath
 *   Remove        two-step, unchanged
 *
 * Design notes carried over from the 2026-07-26 audit: sizes to content;
 * identity is a left-aligned row; no Close button; Remove is a two-step.
 */
/** No open/close animation. Web only — see the note in the component. */
const INSTANT = { duration: 0 } as const;

export function ReferralDetailSheet({
  isOpen,
  onClose,
  item,
  onNudge,
  onRemove,
  startConfirming = false,
  nudgeTemplate = null,
  testID,
}: ReferralDetailSheetProps) {
  const sheetRef = useRef<BottomSheet>(null);
  const insets = useSafeAreaInsets();
  // Dynamic sizing stops at 90% of the screen; past that the body scrolls,
  // so the actions at the bottom stay reachable however many steps a deal
  // has or however long its nudge text runs.
  const { height: windowHeight } = useWindowDimensions();

  // A sheet that slides up from the bottom edge is a phone idiom. On web the
  // slide is slow enough that the panel — already emptied, because the parent
  // clears its item the moment Remove fires — sits over the undo tombstone
  // for most of the 8s window, behind a dimmed backdrop. So the recovery
  // affordance the confirmation exists to provide is unreachable there.
  //
  // On web it opens and closes instantly and is constrained to a dialog-width
  // column instead of spanning the viewport.
  const isWeb = Platform.OS === 'web';
  /**
   * Which referral the local confirm decision applies to. Derived during
   * render rather than synced by an effect: an effect runs AFTER commit, so
   * swapping the sheet's item left one frame where the NEW person rendered
   * with the OLD person's confirm mode — and the red button in that frame
   * targets the new person. Keying the decision to a referral id makes a
   * mismatched id fall back to the prop in the same render, so the stale
   * frame can't exist.
   */
  const [confirmFor, setConfirmFor] = useState<{ id: number; confirming: boolean } | null>(null);

  useEffect(() => {
    if (!sheetRef.current) return;
    if (isOpen) {
      sheetRef.current.snapToIndex(0);
    } else {
      sheetRef.current.close();
    }
  }, [isOpen]);

  // A closed sheet never confirms — reopening mid-confirm would be a
  // jump-scare for whoever opens it next. Otherwise the local decision wins
  // only for the referral it was made about; anyone else falls back to
  // whatever the opener asked for (swipe = true, row tap = false).
  const confirming =
    isOpen && item
      ? confirmFor?.id === item.referral_id
        ? confirmFor.confirming
        : startConfirming
      : false;

  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) onClose();
    },
    [onClose],
  );

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} appearsOnIndex={0} disappearsOnIndex={-1} />
    ),
    [],
  );

  const canNudge = item ? canNudgeItem(item) : false;
  const canRemove = item && onRemove ? canRemoveItem(item) : false;
  const status = item ? statusFor(item) : null;
  const preview = item && canNudge && nudgeTemplate ? previewFor(nudgeTemplate, item) : null;

  return (
    <BottomSheet
      ref={sheetRef}
      index={isOpen ? 0 : -1}
      enableDynamicSizing
      maxDynamicContentSize={Math.round(windowHeight * 0.9)}
      enablePanDownToClose
      animationConfigs={isWeb ? INSTANT : undefined}
      style={isWeb ? styles.sheetWeb : undefined}
      backdropComponent={renderBackdrop}
      onChange={handleSheetChange}
      handleIndicatorStyle={styles.handle}
      backgroundStyle={styles.background}
    >
      {/* Scrolls: the steps, the status card and the message preview can
          outgrow a small screen (or large text), and the actions sit at the
          bottom. Dynamic sizing alone would leave them unreachable. */}
      <BottomSheetScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + spacing.lg }]}
        testID={testID}
      >
        {item && status ? (
          <>
            <View style={styles.identity}>
              <Avatar
                initials={initialsFor(item.name)}
                size={48}
                tone={status.tone === 'not_started' ? 'frost' : 'green'}
              />
              <View style={styles.identityText}>
                <Heading size="2xl">{item.name}</Heading>
                <View style={styles.metaRow}>
                  <Text variant="caption" color="textMuted" testID="referral-detail-meta">
                    {metaFor(item)}
                  </Text>
                  {item.source_label ? (
                    <View style={styles.channelChip}>
                      <Text style={styles.channelChipText}>{item.source_label}</Text>
                    </View>
                  ) : null}
                </View>
              </View>
            </View>

            <View
              style={[styles.statusCard, STATUS_STYLES[status.tone]]}
              testID="referral-detail-status"
            >
              <Text style={styles.statusTitle}>{status.title}</Text>
              <Text style={styles.statusBody}>{status.body}</Text>
            </View>

            {item.progress && item.progress.steps.length > 0 ? (
              <View testID="referral-detail-steps">
                <Overline>Their steps</Overline>
                {item.progress.steps.map((step, index) => (
                  <StepRow
                    key={step.key}
                    step={step}
                    isLast={index === item.progress!.steps.length - 1}
                  />
                ))}
              </View>
            ) : null}

            {/* `canRemove` gates the confirm too, not just the button that
                opens it: a swipe can ask to confirm a row the server would
                refuse, and the destructive button must not exist for it. */}
            {confirming && canRemove ? (
              <View style={styles.block}>
                <Text variant="bodyBold" color="deepGreen">
                  {`Remove ${item.first_name} from your list?`}
                </Text>
                <Text variant="caption" color="textMuted">
                  They stop showing here. If they ever finish, you still get paid.
                </Text>
                <View style={styles.confirmActions}>
                  <View style={styles.confirmButton}>
                    <Button
                      label="Cancel"
                      variant="outline"
                      size="base"
                      fullWidth
                      onPress={() => setConfirmFor({ id: item.referral_id, confirming: false })}
                      testID="referral-detail-remove-cancel"
                    />
                  </View>
                  <Pressable
                    onPress={() => {
                      setConfirmFor({ id: item.referral_id, confirming: false });
                      onRemove?.(item);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Yes, remove ${item.first_name}`}
                    testID="referral-detail-remove-confirm"
                    style={({ pressed }) => [
                      styles.confirmButton,
                      styles.destructive,
                      pressed && styles.destructivePressed,
                    ]}
                  >
                    <Text style={styles.destructiveText}>Remove</Text>
                  </Pressable>
                </View>
              </View>
            ) : (
              (canNudge || canRemove) && (
                <View style={styles.block}>
                  {canNudge ? (
                    <Button
                      label={`Text ${item.first_name}`}
                      variant="primary"
                      size="base"
                      fullWidth
                      leadingIcon={MessageCircle}
                      onPress={() => onNudge(item)}
                      testID="referral-detail-nudge"
                    />
                  ) : null}
                  {preview ? (
                    <View style={styles.preview} testID="referral-detail-preview">
                      <MessageCircle size={16} color={colors.textMuted} strokeWidth={2} />
                      <View style={styles.previewText}>
                        <Text variant="caption" color="charcoal">{`“${preview}”`}</Text>
                        <Text variant="caption" color="textMuted">
                          Prefilled · edit before sending
                        </Text>
                      </View>
                    </View>
                  ) : null}
                  {canRemove ? (
                    <Pressable
                      onPress={() => setConfirmFor({ id: item.referral_id, confirming: true })}
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${item.first_name} from your list`}
                      hitSlop={8}
                      style={({ pressed }) => [styles.removeRow, pressed && styles.removePressed]}
                      testID="referral-detail-remove"
                    >
                      <Text style={styles.removeText}>Remove from list</Text>
                    </Pressable>
                  ) : null}
                </View>
              )
            )}
          </>
        ) : null}
      </BottomSheetScrollView>
    </BottomSheet>
  );
}

interface StepRowProps {
  step: FriendStep;
  isLast: boolean;
}

/**
 * One of the friend's steps. Confirmed: green check, the ambassador's
 * words for it, the day. Waiting: amber ring, the step as the deal names
 * it, "next". Locked: grey ring. Overdue is the status card's sentence
 * above; a bare "nothing since" on the step they have NOT done read as a
 * dangling clause and repeated the card.
 */
function StepRow({ step, isLast }: StepRowProps) {
  const confirmed = step.state === 'confirmed';
  const waiting = step.state === 'waiting';
  const right = confirmed && step.at ? formatDay(step.at) : waiting ? 'next' : '';
  return (
    <View
      style={[styles.stepRow, !isLast && styles.stepDivider]}
      testID={`referral-detail-step-${step.key}`}
    >
      {confirmed ? (
        <View style={[styles.stepDot, styles.stepDotDone]}>
          <Check size={12} color={colors.deepGreen} strokeWidth={3} />
        </View>
      ) : waiting ? (
        <View style={[styles.stepDot, styles.stepDotWaiting]}>
          <View style={styles.stepDotWaitingInner} />
        </View>
      ) : (
        <View style={[styles.stepDot, styles.stepDotLocked]} />
      )}
      <Text
        style={[
          styles.stepLabel,
          confirmed
            ? styles.stepLabelDone
            : waiting
              ? styles.stepLabelWaiting
              : styles.stepLabelLocked,
        ]}
        numberOfLines={2}
      >
        {confirmed ? step.label : step.name}
      </Text>
      {right ? (
        <Text variant="caption" color="textMuted" style={styles.stepRight}>
          {right}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Nudging opens iOS Messages, so it needs a phone and only makes sense
 * while the person can still finish (`canStillFinish`: not paid, not
 * rejected or expired, not removed from the list — a removed friend's
 * sheet used to offer Text and land on an empty nudge screen). Web has
 * no Messages composer.
 */
export function canNudgeItem(item: EveryoneFeedItem): boolean {
  return canStillFinish(item) && Boolean(item.phone_e164) && Platform.OS !== 'web';
}

/**
 * Only in-progress rows are removable. Paid rows are the scoreboard, and
 * the server rejects hiding them — see BUSINESS_RULES.md "Removing a
 * referral". Already-removed rows aren't offered it twice. Hiding is
 * global across deals, so the server also says per row whether it would
 * accept (`can_hide`): a friend paid on Checking is "in progress" on the
 * Invest card yet cannot be removed. Absent against an older server.
 */
export function canRemoveItem(item: EveryoneFeedItem): boolean {
  return item.stage === 'in_progress' && !item.hidden && item.can_hide !== false;
}

/**
 * One line of context under the name: the last thing they did and the
 * day ("Opened SoFi Sep 9", ", nothing since" when overdue). Against an
 * older server, the signup day (and the payout day when paid).
 */
export function metaFor(item: EveryoneFeedItem, now = Date.now()): string {
  const progress = item.progress;
  if (progress) {
    const line = stepLineFor(progress, now);
    return progress.tone === 'overdue' ? `${line}, nothing since` : line;
  }
  const signedUp = item.signed_up_at ? `Signed up ${formatRelative(item.signed_up_at, now)}` : null;
  if (item.stage === 'paid' && item.paid_at) {
    const paid = `Paid ${formatRelative(item.paid_at, now)}`;
    return signedUp ? `${signedUp} · ${paid}` : paid;
  }
  return signedUp ?? 'Signed up recently';
}

export interface SheetStatus {
  tone: FriendProgressTone;
  title: string;
  body: string;
}

/**
 * The status card: the row's two lines as a sentence, tinted by tone.
 * Every noun and number is the server's; only the connective tissue is
 * authored here, once per tone.
 */
export function statusFor(item: EveryoneFeedItem, now = Date.now()): SheetStatus {
  const progress = item.progress;
  if (!progress) {
    return item.stage === 'paid'
      ? { tone: 'paid', title: 'Paid you', body: metaFor(item, now) }
      : { tone: 'active', title: 'In progress', body: metaFor(item, now) };
  }
  const did = stepLineFor(progress, now);
  switch (progress.tone) {
    case 'paid':
      // No "it's in your balance": a cashed-out payout is still a payout.
      return { tone: 'paid', title: progress.next_label, body: `${did}.` };
    case 'overdue':
      // The window is the server's (`stuck_since`); the copy names no number.
      return {
        tone: 'overdue',
        title: progress.next_label,
        body: `${did} and nothing since. That's longer than usual, so a text is worth it.`,
      };
    case 'failed':
      return {
        tone: 'failed',
        title: progress.next_label,
        body: `${did}. Nothing more to do here.`,
      };
    case 'not_started':
      return {
        tone: 'not_started',
        title: progress.next_label,
        body: `${did}. A text with your link gets them going.`,
      };
    case 'active':
    default:
      return { tone: 'active', title: progress.next_label, body: `${did}.` };
  }
}

/**
 * The nudge text as it will be sent to this person, merge tags filled in.
 * `{{your_link}}` stays literal when the row has no share URL — the nudge
 * screen skips those recipients, and showing the gap beats hiding it.
 */
export function previewFor(template: string, item: EveryoneFeedItem): string {
  return renderTemplate(template.trim(), {
    name: item.name,
    first_name: item.first_name,
    tracked_url: item.share_url ?? undefined,
  });
}

const styles = StyleSheet.create({
  // Web only: keep the sheet to a dialog-width column instead of letting it
  // stretch the full viewport, where the identity block and the action end up
  // hundreds of pixels apart and read as unrelated.
  sheetWeb: {
    maxWidth: 520,
    // The sheet container is absolutely positioned, so alignSelf does
    // nothing — pinning both edges and letting auto margins split the
    // remainder is what actually centers it.
    left: 0,
    right: 0,
    marginHorizontal: 'auto',
  },
  background: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
  },
  handle: {
    backgroundColor: colors.border,
    width: 36,
  },
  content: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    gap: spacing.lg,
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  identityText: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  channelChip: {
    borderRadius: radii.full,
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: colors.brightGreenSoft,
  },
  channelChipText: {
    color: colors.deepGreen,
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
  statusCard: {
    paddingVertical: 14,
    paddingHorizontal: spacing.base,
    // 14: the Refer friends card's panel/list/status radius (mock P1e/D1),
    // between radii.base and radii.lg. Documented drift, same in WhoClickedCard.
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    gap: 2,
  },
  statusPaid: { backgroundColor: colors.paidPillBg, borderColor: colors.paidPillBorder },
  // Active is the white card; never started is the grey one, the same
  // deepGreen-vs-muted split the row draws in its text.
  statusActive: { backgroundColor: colors.surface, borderColor: colors.border },
  statusOverdue: { backgroundColor: colors.warnPillBg, borderColor: colors.warnPillBorder },
  statusFailed: { backgroundColor: colors.overduePillBg, borderColor: colors.overduePillText },
  statusNotStarted: { backgroundColor: colors.neutralPillBg, borderColor: colors.border },
  statusTitle: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.base,
    lineHeight: 22,
    color: colors.deepGreen,
  },
  statusBody: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.charcoal,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: 10,
  },
  stepDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  stepDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepDotDone: { backgroundColor: colors.electricGreen },
  stepDotWaiting: { borderWidth: 2.5, borderColor: colors.warning },
  stepDotWaitingInner: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.warning },
  stepDotLocked: { borderWidth: 2, borderColor: colors.track },
  stepLabel: {
    flex: 1,
    fontSize: fontSizes.base,
    lineHeight: 20,
  },
  stepLabelDone: { fontFamily: fonts.bodyMedium, color: colors.charcoal },
  stepLabelWaiting: { fontFamily: fonts.bodyBold, color: colors.warnPillText },
  stepLabelLocked: { fontFamily: fonts.bodyMedium, color: colors.textMuted },
  stepRight: {
    textAlign: 'right',
    flexShrink: 0,
  },
  block: {
    gap: spacing.sm,
  },
  preview: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderRadius: radii.base,
    backgroundColor: colors.neutralPillBg,
  },
  previewText: {
    flex: 1,
    gap: 2,
  },
  removeRow: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  removePressed: {
    opacity: 0.6,
  },
  removeText: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.base,
    color: colors.danger,
  },
  confirmActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  confirmButton: {
    flex: 1,
  },
  destructive: {
    minHeight: 48,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.danger,
  },
  destructivePressed: {
    opacity: 0.8,
  },
  destructiveText: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.base,
    color: colors.frost,
  },
});

// Declared after `styles` — references it at module load. Kept in step with
// TONE_TEXT in WhoClickedRow.tsx (the row's line-2 color per tone).
const STATUS_STYLES: Record<FriendProgressTone, object> = {
  paid: styles.statusPaid,
  active: styles.statusActive,
  overdue: styles.statusOverdue,
  failed: styles.statusFailed,
  not_started: styles.statusNotStarted,
};
